import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { ShieldAlert, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * RecentPrivilegedActionsWidget
 * ─────────────────────────────
 * V22.9 P1 audit-of-audit visibility. Surfaces the last 24 h of
 * `admin.cross_tenant_audit_read` rows so SOC 2 control owners actually
 * SEE every privileged cross-tenant read instead of having to query the
 * audit log by hand. Powered by `_tenant_scope_query()` in
 * /app/backend/routers/audit.py.
 */
function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function RecentPrivilegedActionsWidget() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgNameById, setOrgNameById] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [logsRes, orgsRes] = await Promise.allSettled([
          api.get("/audit/logs", {
            params: { action: "admin.cross_tenant_audit_read", days: 1, limit: 10 },
          }),
          api.get("/admin/organizations"),
        ]);
        if (cancelled) return;
        const logs = logsRes.status === "fulfilled" ? (logsRes.value.data?.logs || []) : [];
        setRows(logs);
        if (orgsRes.status === "fulfilled") {
          const map = {};
          for (const o of orgsRes.value.data?.organizations || orgsRes.value.data || []) {
            if (o?.id) map[o.id] = o.name || o.org_code || o.id;
          }
          setOrgNameById(map);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [api]);

  return (
    <Card data-testid="recent-privileged-actions-widget" className="border-amber-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="p-1.5 rounded-lg bg-amber-100">
              <ShieldAlert className="w-4 h-4 text-amber-700" />
            </span>
            Recent Privileged Actions
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[0.65rem] ml-1">
              SOC 2 · CC7.2
            </Badge>
            <span className="ml-1 text-sm font-normal text-slate-400">
              ({rows.length} cross-tenant read{rows.length === 1 ? "" : "s"} · last 24 h)
            </span>
          </CardTitle>
          <button
            onClick={() => navigate("/audit-logs?action=admin.cross_tenant_audit_read")}
            className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
            data-testid="recent-privileged-actions-view-all"
          >
            View in Audit Logs <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading privileged-action trail…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4" data-testid="recent-privileged-actions-empty">
            No cross-tenant reads in the last 24 hours. Tenant isolation is fully respected.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => {
              const target = r.resource_id;
              const targetName = (target && orgNameById[target]) || target || "—";
              const who = r.user_name || r.user_email || r.user_id;
              return (
                <div
                  key={r.id}
                  data-testid={`privileged-row-${r.id}`}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700 text-[0.65rem] font-bold flex-shrink-0">
                    ADM
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800">
                      <span className="font-semibold">{who}</span>
                      <span className="text-slate-500"> read audit logs of </span>
                      <span className="font-semibold text-amber-800" title={target}>{targetName}</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      <code className="bg-slate-50 border border-slate-200 rounded px-1 py-0.5 text-[0.65rem] mr-1.5">
                        admin.cross_tenant_audit_read
                      </code>
                      {timeAgo(r.timestamp)}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/audit-logs?org_id=${target}`)}
                    className="text-[0.7rem] font-semibold text-sky-700 hover:text-sky-900 px-2 py-1 rounded hover:bg-sky-50 flex-shrink-0"
                    data-testid={`privileged-row-open-${r.id}`}
                  >
                    Open trail
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
